import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import PublicNav from '../components/PublicNav';

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    basePrice: 15,
    autopayPrice: 13,
    perVisit: 2,
    description: 'Perfect for solo operators',
    features: [
      { text: 'Unlimited customers', included: true },
      { text: 'Service Mode', included: true },
      { text: 'Customer intake forms', included: true },
      { text: 'Route optimization', included: true },
      { text: 'Weather forecasts', included: true },
      { text: 'Service history', included: true },
      { text: 'Referral discount system', included: false },
      { text: 'Expense tracking', included: false },
      { text: 'Square integration', included: false },
      { text: 'Customer portal', included: false },
      { text: 'Multi-user accounts', included: false },
    ],
    cta: 'Start Free Trial',
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    basePrice: 35,
    autopayPrice: 30,
    perVisit: 1,
    description: 'For growing businesses',
    features: [
      { text: 'Everything in Basic, plus:', included: true, isHeader: true },
      { text: 'Referral discount system', included: true },
      { text: 'Expense tracking', included: true },
      { text: 'Square integration', included: true },
      { text: 'Profit & loss reports', included: true },
      { text: 'Payment status tracking', included: true },
      { text: 'Customer portal', included: false },
      { text: 'Multi-user accounts', included: false },
      { text: 'Real-time GPS tracking', included: false },
      { text: 'Advanced analytics', included: false },
    ],
    cta: 'Start Free Trial',
    highlighted: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    basePrice: 69,
    autopayPrice: 59,
    perVisit: null, // Unlimited
    description: 'For small teams (up to 5 users)',
    badge: 'Most Popular',
    features: [
      { text: 'Everything in Pro, plus:', included: true, isHeader: true },
      { text: 'Unlimited visits included', included: true, highlight: true },
      { text: 'Customer portal', included: true },
      { text: 'Multi-user accounts (up to 5)', included: true },
      { text: 'Real-time GPS tracking', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'Route profitability reports', included: true },
      { text: 'Unlimited contractors', included: false },
      { text: 'Priority support', included: false },
    ],
    cta: 'Start Free Trial',
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    basePrice: 129,
    autopayPrice: 110,
    perVisit: null, // Unlimited
    description: 'For large operations',
    features: [
      { text: 'Everything in Premium, plus:', included: true, isHeader: true },
      { text: 'Unlimited visits included', included: true, highlight: true },
      { text: 'Unlimited contractors/users', included: true },
      { text: 'Priority support', included: true },
      { text: 'Custom branding', included: true },
      { text: 'API access', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Custom integrations', included: true },
    ],
    cta: 'Start Free Trial',
    highlighted: false,
  },
];

const FAQS = [
  {
    question: 'How does the per-visit pricing work?',
    answer: 'Basic and Pro plans have a low monthly base fee plus a small per-visit charge. A "visit" is counted each time you complete a route. This keeps costs low during slow months and scales with your business during busy periods.',
  },
  {
    question: 'What counts as a "visit"?',
    answer: 'A visit is counted when you complete a route in Service Mode. If you run the same route multiple times in one week, each completion counts as one visit. Most operators average 50-150 visits per season.',
  },
  {
    question: 'How long is the free trial?',
    answer: 'Every plan starts with a free trial that includes 1 complete service visit. This lets you experience LawnTrack in real conditions before committing.',
  },
  {
    question: 'What is Autopay and how do I save 15%?',
    answer: 'Autopay automatically charges your card each month. In exchange for the convenience and guaranteed payment, we offer a 15% discount on your base monthly fee. You can enable or disable Autopay anytime from your account settings.',
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Absolutely! You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the start of your next billing cycle. Any unused per-visit credits carry over.',
  },
  {
    question: 'Is there a contract or commitment?',
    answer: 'No contracts, no commitments. All plans are month-to-month and you can cancel anytime. We also offer seasonal passes for operators who want to lock in their rate.',
  },
  {
    question: 'How many users can I add?',
    answer: 'Basic and Pro plans are single-user. Premium includes up to 5 users (perfect for a small crew). Enterprise includes unlimited users for large operations with multiple crews.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor. Enterprise customers can also pay by invoice.',
  },
];

// Feature data for mobile accordion view
const COMPARISON_DATA = [
  {
    id: 'basic',
    name: 'Basic',
    price: { monthly: 15, autopay: 13 },
    perVisit: '$2/visit',
    features: [
      { name: 'Unlimited customers', included: true },
      { name: 'Service Mode', included: true },
      { name: 'Route optimization', included: true },
      { name: 'Weather forecasts', included: true },
      { name: 'Customer intake forms', included: true },
      { name: 'Service history', included: true },
      { name: 'Referral discount system', included: false },
      { name: 'Expense tracking', included: false },
      { name: 'Square integration', included: false },
      { name: 'Customer portal', included: false },
      { name: 'Multi-user accounts', included: false },
      { name: 'Real-time GPS tracking', included: false },
      { name: 'Priority support', included: false },
      { name: 'Custom branding', included: false },
      { name: 'API access', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: { monthly: 35, autopay: 30 },
    perVisit: '$1/visit',
    features: [
      { name: 'Unlimited customers', included: true },
      { name: 'Service Mode', included: true },
      { name: 'Route optimization', included: true },
      { name: 'Weather forecasts', included: true },
      { name: 'Customer intake forms', included: true },
      { name: 'Service history', included: true },
      { name: 'Referral discount system', included: true },
      { name: 'Expense tracking', included: true },
      { name: 'Square integration', included: true },
      { name: 'Profit & loss reports', included: true },
      { name: 'Customer portal', included: false },
      { name: 'Multi-user accounts', included: false },
      { name: 'Real-time GPS tracking', included: false },
      { name: 'Priority support', included: false },
      { name: 'Custom branding', included: false },
      { name: 'API access', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: { monthly: 69, autopay: 59 },
    perVisit: 'Unlimited',
    badge: 'Most Popular',
    features: [
      { name: 'Unlimited customers', included: true },
      { name: 'Service Mode', included: true },
      { name: 'Route optimization', included: true },
      { name: 'Weather forecasts', included: true },
      { name: 'Customer intake forms', included: true },
      { name: 'Service history', included: true },
      { name: 'Referral discount system', included: true },
      { name: 'Expense tracking', included: true },
      { name: 'Square integration', included: true },
      { name: 'Profit & loss reports', included: true },
      { name: 'Customer portal', included: true },
      { name: 'Multi-user accounts', included: true, note: 'Up to 5' },
      { name: 'Real-time GPS tracking', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'Priority support', included: false },
      { name: 'Custom branding', included: false },
      { name: 'API access', included: false },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: { monthly: 129, autopay: 110 },
    perVisit: 'Unlimited',
    features: [
      { name: 'Unlimited customers', included: true },
      { name: 'Service Mode', included: true },
      { name: 'Route optimization', included: true },
      { name: 'Weather forecasts', included: true },
      { name: 'Customer intake forms', included: true },
      { name: 'Service history', included: true },
      { name: 'Referral discount system', included: true },
      { name: 'Expense tracking', included: true },
      { name: 'Square integration', included: true },
      { name: 'Profit & loss reports', included: true },
      { name: 'Customer portal', included: true },
      { name: 'Multi-user accounts', included: true, note: 'Unlimited' },
      { name: 'Real-time GPS tracking', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'Priority support', included: true },
      { name: 'Custom branding', included: true },
      { name: 'API access', included: true },
    ],
  },
];

function Pricing() {
  const navigate = useNavigate();
  const { getAppName } = useConfig();
  const [openFaq, setOpenFaq] = useState(null);
  const [openComparison, setOpenComparison] = useState(null);
  const [autopay, setAutopay] = useState(true);

  const handleStartTrial = (tierId) => {
    navigate(`/register?plan=${tierId}`);
  };

  const getPrice = (tier) => {
    return autopay ? tier.autopayPrice : tier.basePrice;
  };

  const formatPrice = (price) => {
    // Format to show .XX only if not a whole number
    if (price % 1 === 0) {
      return price.toString();
    }
    return price.toFixed(2);
  };

  return (
    <div className="pricing-page">
      <PublicNav />

      {/* Hero Section */}
      <section className="pricing-hero">
        <h1 className="pricing-title">Simple, Flexible Pricing</h1>
        <p className="pricing-subtitle">
          Pay for what you use. Start with a free trial, upgrade anytime.
        </p>

        {/* Autopay Toggle */}
        <div className="billing-toggle">
          <button
            className={`toggle-btn ${!autopay ? 'active' : ''}`}
            onClick={() => setAutopay(false)}
          >
            Pay Monthly
          </button>
          <button
            className={`toggle-btn ${autopay ? 'active' : ''}`}
            onClick={() => setAutopay(true)}
          >
            Autopay
            <span className="save-badge">Save 15%</span>
          </button>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards-section">
        <div className="pricing-cards">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`pricing-card ${tier.highlighted ? 'highlighted' : ''}`}
            >
              {tier.badge && <div className="tier-badge">{tier.badge}</div>}

              <div className="card-header">
                <h2 className="tier-name">{tier.name}</h2>
                <p className="tier-description">{tier.description}</p>
              </div>

              <div className="price-container">
                <span className="price-currency">$</span>
                <span className="price-amount">{formatPrice(getPrice(tier))}</span>
                <span className="price-period">/mo</span>
              </div>

              {tier.perVisit ? (
                <p className="per-visit-note">+ ${tier.perVisit}/visit</p>
              ) : (
                <p className="unlimited-note">Unlimited visits included</p>
              )}

              <button
                className={`cta-button ${tier.highlighted ? 'cta-highlighted' : ''}`}
                onClick={() => handleStartTrial(tier.id)}
              >
                {tier.cta}
              </button>

              <ul className="feature-list">
                {tier.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className={`feature-item ${feature.included ? 'included' : 'excluded'} ${feature.isHeader ? 'feature-header' : ''} ${feature.highlight ? 'feature-highlight' : ''}`}
                  >
                    {!feature.isHeader && (
                      <span className="feature-icon">
                        {feature.included ? '✓' : '—'}
                      </span>
                    )}
                    <span className="feature-text">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Example */}
      <section className="example-section">
        <div className="example-content">
          <h3>💡 Example: What would I pay?</h3>
          <div className="example-cards">
            <div className="example-card">
              <h4>Small Operation</h4>
              <p className="example-scenario">50 visits/season</p>
              <p className="example-calc">
                Basic: ${autopay ? '13' : '15'}/mo × 6 + (50 × $2) = <strong>${autopay ? '178' : '190'}/season</strong>
              </p>
            </div>
            <div className="example-card">
              <h4>Growing Business</h4>
              <p className="example-scenario">100 visits/season</p>
              <p className="example-calc">
                Pro: ${autopay ? '30' : '35'}/mo × 6 + (100 × $1) = <strong>${autopay ? '280' : '310'}/season</strong>
              </p>
            </div>
            <div className="example-card featured">
              <h4>High Volume</h4>
              <p className="example-scenario">150+ visits/season</p>
              <p className="example-calc">
                Premium: ${autopay ? '59' : '69'}/mo × 6 = <strong>${autopay ? '354' : '414'}/season</strong>
              </p>
              <p className="example-note">Unlimited visits, no surprises!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table - Desktop */}
      <section className="comparison-section">
        <h2 className="section-title">Compare All Features</h2>

        {/* Mobile Accordion View */}
        <div className="comparison-accordion-mobile">
          {COMPARISON_DATA.map((plan, idx) => (
            <div
              key={plan.id}
              className={`comparison-accordion-item ${openComparison === idx ? 'open' : ''} ${plan.badge ? 'highlighted' : ''}`}
            >
              <button
                className="comparison-accordion-header"
                onClick={() => setOpenComparison(openComparison === idx ? null : idx)}
              >
                <div className="accordion-plan-info">
                  <span className="accordion-plan-name">{plan.name}</span>
                  {plan.badge && <span className="accordion-badge">{plan.badge}</span>}
                  <span className="accordion-plan-price">
                    ${autopay ? plan.price.autopay : plan.price.monthly}/mo
                    {plan.perVisit !== 'Unlimited' && <span className="accordion-per-visit"> + {plan.perVisit}</span>}
                    {plan.perVisit === 'Unlimited' && <span className="accordion-unlimited">Unlimited visits</span>}
                  </span>
                </div>
                <span className="accordion-icon">{openComparison === idx ? '−' : '+'}</span>
              </button>
              {openComparison === idx && (
                <div className="comparison-accordion-content">
                  <ul className="accordion-feature-list">
                    {plan.features.map((feature, fIdx) => (
                      <li key={fIdx} className={feature.included ? 'included' : 'excluded'}>
                        <span className="accordion-feature-icon">{feature.included ? '✓' : '—'}</span>
                        <span className="accordion-feature-name">
                          {feature.name}
                          {feature.note && <span className="feature-note"> ({feature.note})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="feature-col">Feature</th>
                <th className="tier-col">Basic</th>
                <th className="tier-col">Pro</th>
                <th className="tier-col highlight-col">Premium</th>
                <th className="tier-col">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr className="category-row">
                <td colSpan="5">Pricing</td>
              </tr>
              <tr>
                <td>Monthly base (Autopay)</td>
                <td>$13</td>
                <td>$30</td>
                <td className="highlight-col">$59</td>
                <td>$110</td>
              </tr>
              <tr>
                <td>Per-visit fee</td>
                <td>$2</td>
                <td>$1</td>
                <td className="highlight-col"><span className="unlimited-badge">Unlimited</span></td>
                <td><span className="unlimited-badge">Unlimited</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan="5">Core Features</td>
              </tr>
              <tr>
                <td>Unlimited customers</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Service Mode</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Route optimization</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Weather forecasts</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Customer intake forms</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Service history</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan="5">Business Tools</td>
              </tr>
              <tr>
                <td>Referral discount system</td>
                <td><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Expense tracking</td>
                <td><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Square integration</td>
                <td><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Profit & loss reports</td>
                <td><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan="5">Advanced Features</td>
              </tr>
              <tr>
                <td>Customer portal</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Multi-user accounts</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col">Up to 5</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td>Real-time GPS tracking</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Advanced analytics</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan="5">Enterprise Features</td>
              </tr>
              <tr>
                <td>Priority support</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Custom branding</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>API access</td>
                <td><span className="dash">—</span></td>
                <td><span className="dash">—</span></td>
                <td className="highlight-col"><span className="dash">—</span></td>
                <td><span className="check">✓</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-list">
          {FAQS.map((faq, idx) => (
            <div
              key={idx}
              className={`faq-item ${openFaq === idx ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openFaq === idx ? '−' : '+'}</span>
              </button>
              {openFaq === idx && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <h2>Ready to streamline your lawn care business?</h2>
        <p>Join hundreds of lawn care professionals who trust LawnTrack.</p>
        <button
          className="cta-button cta-large"
          onClick={() => navigate('/register')}
        >
          Start Your Free Trial
        </button>
        <p className="cta-note">No credit card required • 1 free visit • Cancel anytime</p>
      </section>

      {/* Footer */}
      <footer className="pricing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">🌿</span>
            <span>{getAppName()}</span>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} {getAppName()}. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        .pricing-page {
          min-height: 100vh;
          background: #ffffff;
          padding-top: 64px;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        /* Hero */
        .pricing-hero {
          text-align: center;
          padding: 60px 24px 40px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
        }

        .pricing-title {
          font-size: 2.75rem;
          font-weight: 800;
          color: #111827;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .pricing-subtitle {
          font-size: 1.25rem;
          color: #6b7280;
          max-width: 500px;
          margin: 0 auto 32px;
        }

        /* Billing Toggle */
        .billing-toggle {
          display: inline-flex;
          background: #f3f4f6;
          border-radius: 12px;
          padding: 4px;
          gap: 4px;
        }

        .toggle-btn {
          padding: 12px 24px;
          border: none;
          background: transparent;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .toggle-btn.active {
          background: white;
          color: #111827;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .save-badge {
          background: #dcfce7;
          color: #166534;
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
        }

        /* Pricing Cards */
        .pricing-cards-section {
          padding: 20px 24px 60px;
          max-width: 1280px;
          margin: 0 auto;
        }

        .pricing-cards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          align-items: start;
        }

        .pricing-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 28px 24px;
          position: relative;
          transition: all 0.3s ease;
        }

        .pricing-card:hover {
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          transform: translateY(-4px);
        }

        .pricing-card.highlighted {
          border: 2px solid var(--color-primary);
          box-shadow: 0 20px 40px rgba(30, 136, 229, 0.15);
          transform: scale(1.03);
          z-index: 1;
        }

        .pricing-card.highlighted:hover {
          transform: scale(1.03) translateY(-4px);
        }

        .tier-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--color-primary);
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          white-space: nowrap;
        }

        .card-header {
          text-align: center;
          margin-bottom: 20px;
        }

        .tier-name {
          font-size: 1.35rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 6px;
        }

        .tier-description {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .price-container {
          text-align: center;
          margin-bottom: 4px;
        }

        .price-currency {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          vertical-align: top;
          position: relative;
          top: 8px;
        }

        .price-amount {
          font-size: 3rem;
          font-weight: 800;
          color: #111827;
          line-height: 1;
          letter-spacing: -0.02em;
        }

        .price-period {
          font-size: 1rem;
          color: #6b7280;
          font-weight: 500;
        }

        .per-visit-note {
          text-align: center;
          font-size: 0.95rem;
          color: #f59e0b;
          font-weight: 600;
          margin: 0 0 16px 0;
        }

        .unlimited-note {
          text-align: center;
          font-size: 0.95rem;
          color: #16a34a;
          font-weight: 600;
          margin: 0 0 16px 0;
        }

        .cta-button {
          display: block;
          width: 100%;
          padding: 12px 20px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #f3f4f6;
          color: #374151;
          margin: 0 0 24px 0;
        }

        .cta-button:hover {
          background: #e5e7eb;
        }

        .cta-button.cta-highlighted {
          background: var(--color-primary);
          color: white;
        }

        .cta-button.cta-highlighted:hover {
          background: var(--color-primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(30, 136, 229, 0.4);
        }


        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 8px 0;
          border-top: 1px solid #f3f4f6;
          font-size: 0.875rem;
        }

        .feature-item.feature-header {
          font-weight: 600;
          color: #111827;
          border-top: none;
          padding-top: 0;
        }

        .feature-item.feature-highlight {
          color: #16a34a;
          font-weight: 600;
        }

        .feature-item.included {
          color: #374151;
        }

        .feature-item.excluded {
          color: #9ca3af;
        }

        .feature-icon {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-size: 0.7rem;
          font-weight: 700;
        }

        .feature-item.included .feature-icon {
          background: #dcfce7;
          color: #166534;
        }

        .feature-item.excluded .feature-icon {
          background: #f3f4f6;
          color: #9ca3af;
        }

        .feature-item.feature-highlight .feature-icon {
          background: #dcfce7;
          color: #166534;
        }

        /* Example Section */
        .example-section {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 48px 24px;
        }

        .example-content {
          max-width: 1000px;
          margin: 0 auto;
        }

        .example-content h3 {
          text-align: center;
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 24px;
        }

        .example-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .example-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        .example-card.featured {
          border: 2px solid var(--color-primary);
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
        }

        .example-card h4 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 8px 0;
        }

        .example-scenario {
          font-size: 0.9rem;
          color: #6b7280;
          margin: 0 0 12px 0;
        }

        .example-calc {
          font-size: 0.95rem;
          color: #374151;
          margin: 0;
        }

        .example-calc strong {
          color: #16a34a;
          font-size: 1.1rem;
        }

        .example-note {
          font-size: 0.85rem;
          color: var(--color-primary);
          font-weight: 500;
          margin: 8px 0 0 0;
        }

        /* Comparison Table */
        .comparison-section {
          padding: 60px 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .section-title {
          text-align: center;
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 40px;
        }

        /* Mobile Accordion - Hidden by default, shown on mobile */
        .comparison-accordion-mobile {
          display: none;
        }

        .comparison-accordion-item {
          background: white;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
        }

        .comparison-accordion-item.highlighted {
          border: 2px solid var(--color-primary);
        }

        .comparison-accordion-header {
          width: 100%;
          padding: 16px 20px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .comparison-accordion-header:active {
          background: #f9fafb;
        }

        .accordion-plan-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .accordion-plan-name {
          font-size: 1.1rem;
          font-weight: 700;
          color: #111827;
        }

        .accordion-badge {
          display: inline-block;
          background: var(--color-primary);
          color: white;
          font-size: 0.65rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          text-transform: uppercase;
          width: fit-content;
        }

        .accordion-plan-price {
          font-size: 0.95rem;
          color: #374151;
          font-weight: 600;
        }

        .accordion-per-visit {
          color: #f59e0b;
          font-weight: 600;
        }

        .accordion-unlimited {
          color: #16a34a;
          font-size: 0.8rem;
          display: block;
          font-weight: 500;
        }

        .accordion-icon {
          font-size: 1.5rem;
          color: #6b7280;
          font-weight: 300;
          flex-shrink: 0;
        }

        .comparison-accordion-content {
          padding: 0 20px 20px;
          border-top: 1px solid #f3f4f6;
        }

        .accordion-feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .accordion-feature-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.9rem;
        }

        .accordion-feature-list li:last-child {
          border-bottom: none;
        }

        .accordion-feature-list li.included {
          color: #374151;
        }

        .accordion-feature-list li.excluded {
          color: #9ca3af;
        }

        .accordion-feature-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-size: 0.7rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .accordion-feature-list li.included .accordion-feature-icon {
          background: #dcfce7;
          color: #166534;
        }

        .accordion-feature-list li.excluded .accordion-feature-icon {
          background: #f3f4f6;
          color: #9ca3af;
        }

        .feature-note {
          color: #6b7280;
          font-size: 0.85rem;
        }

        .comparison-table-wrapper {
          overflow-x: auto;
        }

        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .comparison-table th,
        .comparison-table td {
          padding: 12px 14px;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .comparison-table th {
          font-weight: 600;
          color: #374151;
          background: #f9fafb;
        }

        .comparison-table .feature-col {
          text-align: left;
          width: 30%;
        }

        .comparison-table .tier-col {
          width: 17.5%;
        }

        .comparison-table .highlight-col {
          background: #eff6ff;
        }

        .comparison-table th.highlight-col {
          background: var(--color-primary);
          color: white;
        }

        .category-row td {
          font-weight: 700;
          color: #111827;
          background: #f9fafb;
          text-align: left !important;
          padding-top: 20px;
        }

        .check {
          color: #16a34a;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .dash {
          color: #d1d5db;
          font-size: 1.1rem;
        }

        .unlimited-badge {
          background: #dcfce7;
          color: #166534;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* FAQ Section */
        .faq-section {
          padding: 60px 24px;
          background: #f9fafb;
        }

        .faq-list {
          max-width: 700px;
          margin: 0 auto;
        }

        .faq-item {
          background: white;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .faq-question {
          width: 100%;
          padding: 20px 24px;
          border: none;
          background: none;
          text-align: left;
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .faq-question:hover {
          background: #f9fafb;
        }

        .faq-icon {
          font-size: 1.5rem;
          color: #6b7280;
          font-weight: 300;
        }

        .faq-answer {
          padding: 0 24px 20px;
        }

        .faq-answer p {
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Final CTA */
        .final-cta {
          text-align: center;
          padding: 80px 24px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
        }

        .final-cta h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: white;
        }

        .final-cta > p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 32px;
        }

        .cta-large {
          display: inline-block;
          width: auto;
          padding: 16px 40px;
          font-size: 1.1rem;
          background: white;
          color: var(--color-primary);
          margin: 0;
        }

        .cta-large:hover {
          background: #f8fafc;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
        }

        .cta-note {
          margin-top: 16px;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.9);
        }

        /* Footer */
        .pricing-footer {
          background: #111827;
          color: white;
          padding: 32px 24px;
        }

        .footer-content {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }

        .footer-copy {
          color: #9ca3af;
          font-size: 0.9rem;
        }

        /* Mobile Responsive */

        /* Tablet - 768px to 1100px */
        @media (max-width: 1100px) {
          .pricing-cards {
            grid-template-columns: repeat(2, 1fr);
            max-width: 700px;
            margin: 0 auto;
          }

          .pricing-card.highlighted {
            transform: scale(1.02);
          }

          .pricing-card.highlighted:hover {
            transform: scale(1.02) translateY(-4px);
          }

          .example-cards {
            grid-template-columns: 1fr;
            max-width: 450px;
            margin: 0 auto;
          }
        }

        /* Tablet Portrait - 768px */
        @media (max-width: 768px) {
          .pricing-hero {
            padding: 32px 16px 24px;
          }

          .pricing-title {
            font-size: 1.75rem;
            line-height: 1.2;
          }

          .pricing-subtitle {
            font-size: 1rem;
            margin-bottom: 24px;
          }

          .billing-toggle {
            width: 100%;
            max-width: 320px;
          }

          .toggle-btn {
            flex: 1;
            justify-content: center;
            padding: 14px 16px;
            min-height: 48px;
          }

          .pricing-cards-section {
            padding: 20px 16px 40px;
          }

          .pricing-cards {
            grid-template-columns: 1fr;
            max-width: 100%;
            gap: 16px;
          }

          .pricing-card {
            padding: 24px 20px;
          }

          .pricing-card.highlighted {
            transform: none;
            order: -1;
            border-width: 2px;
          }

          .pricing-card.highlighted:hover {
            transform: none;
          }

          .tier-badge {
            font-size: 0.7rem;
            padding: 5px 12px;
          }

          .tier-name {
            font-size: 1.25rem;
          }

          .tier-description {
            font-size: 0.85rem;
          }

          .price-amount {
            font-size: 2.5rem;
          }

          .price-currency {
            font-size: 1.1rem;
            top: 6px;
          }

          .per-visit-note,
          .unlimited-note {
            font-size: 0.9rem;
          }

          .cta-button {
            padding: 14px 20px;
            font-size: 1rem;
            min-height: 48px;
          }

          .feature-item {
            font-size: 0.9rem;
            padding: 10px 0;
          }

          .feature-icon {
            width: 20px;
            height: 20px;
            font-size: 0.75rem;
          }

          .example-section {
            padding: 32px 16px;
          }

          .example-content h3 {
            font-size: 1.25rem;
            margin-bottom: 20px;
          }

          .example-cards {
            grid-template-columns: 1fr;
            max-width: 100%;
            gap: 12px;
          }

          .example-card {
            padding: 20px;
          }

          .example-card h4 {
            font-size: 1rem;
          }

          .example-scenario {
            font-size: 0.85rem;
          }

          .example-calc {
            font-size: 0.9rem;
          }

          .comparison-section {
            padding: 40px 16px;
          }

          .section-title {
            font-size: 1.5rem;
            margin-bottom: 24px;
          }

          /* Show mobile accordion, hide desktop table */
          .comparison-accordion-mobile {
            display: block;
          }

          .comparison-table-wrapper {
            display: none;
          }

          .faq-section {
            padding: 40px 16px;
          }

          .faq-item {
            margin-bottom: 10px;
          }

          .faq-question {
            padding: 16px 20px;
            font-size: 0.95rem;
            min-height: 56px;
          }

          .faq-icon {
            font-size: 1.25rem;
            flex-shrink: 0;
          }

          .faq-answer {
            padding: 0 20px 16px;
          }

          .faq-answer p {
            font-size: 0.9rem;
            line-height: 1.6;
          }

          .final-cta {
            padding: 48px 16px;
          }

          .final-cta h2 {
            font-size: 1.5rem;
            line-height: 1.3;
          }

          .final-cta > p {
            font-size: 1rem;
            margin-bottom: 24px;
          }

          .cta-large {
            width: 100%;
            max-width: 300px;
            padding: 16px 32px;
          }

          .cta-note {
            font-size: 0.85rem;
          }

          .footer-content {
            flex-direction: column;
            gap: 12px;
            text-align: center;
          }

          .footer-copy {
            font-size: 0.85rem;
          }
        }

        /* Mobile - 375px (iPhone) */
        @media (max-width: 480px) {
          .pricing-hero {
            padding: 24px 12px 20px;
          }

          .pricing-title {
            font-size: 1.5rem;
          }

          .pricing-subtitle {
            font-size: 0.95rem;
            padding: 0 8px;
          }

          .billing-toggle {
            flex-direction: column;
            width: 100%;
            max-width: 100%;
            gap: 6px;
          }

          .toggle-btn {
            width: 100%;
            padding: 14px 20px;
          }

          .save-badge {
            font-size: 0.7rem;
            padding: 2px 6px;
          }

          .pricing-cards-section {
            padding: 16px 12px 32px;
          }

          .pricing-cards {
            gap: 12px;
          }

          .pricing-card {
            padding: 20px 16px;
          }

          .card-header {
            margin-bottom: 16px;
          }

          .tier-name {
            font-size: 1.2rem;
          }

          .price-container {
            margin-bottom: 2px;
          }

          .price-amount {
            font-size: 2.25rem;
          }

          .cta-button {
            margin-bottom: 20px;
          }

          .feature-item {
            font-size: 0.85rem;
            padding: 8px 0;
            gap: 8px;
          }

          .example-section {
            padding: 24px 12px;
          }

          .example-content h3 {
            font-size: 1.1rem;
          }

          .example-card {
            padding: 16px;
          }

          .example-card h4 {
            font-size: 0.95rem;
            margin-bottom: 4px;
          }

          .example-scenario {
            font-size: 0.8rem;
            margin-bottom: 8px;
          }

          .example-calc {
            font-size: 0.85rem;
          }

          .example-calc strong {
            font-size: 1rem;
          }

          .example-note {
            font-size: 0.8rem;
          }

          .comparison-section {
            padding: 32px 12px;
          }

          .section-title {
            font-size: 1.25rem;
          }

          .comparison-table-wrapper {
            margin: 0 -12px;
            padding: 0 12px;
          }

          .comparison-table {
            font-size: 0.75rem;
          }

          .comparison-table th,
          .comparison-table td {
            padding: 8px 6px;
          }

          .unlimited-badge {
            font-size: 0.65rem;
            padding: 2px 6px;
          }

          .faq-section {
            padding: 32px 12px;
          }

          .faq-question {
            padding: 14px 16px;
            font-size: 0.9rem;
            gap: 12px;
          }

          .faq-answer {
            padding: 0 16px 14px;
          }

          .faq-answer p {
            font-size: 0.85rem;
          }

          .final-cta {
            padding: 40px 12px;
          }

          .final-cta h2 {
            font-size: 1.25rem;
            padding: 0 8px;
          }

          .final-cta > p {
            font-size: 0.95rem;
          }

          .cta-large {
            width: 100%;
            max-width: 100%;
            font-size: 1rem;
          }

          .pricing-footer {
            padding: 24px 12px;
          }
        }

        /* Extra small devices */
        @media (max-width: 360px) {
          .pricing-title {
            font-size: 1.35rem;
          }

          .price-amount {
            font-size: 2rem;
          }
        }

        /* Touch device improvements */
        @media (hover: none) and (pointer: coarse) {
          .pricing-card:hover {
            transform: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .pricing-card.highlighted:hover {
            transform: none;
          }

          .cta-button:hover {
            transform: none;
          }

          .cta-button.cta-highlighted:hover {
            transform: none;
            box-shadow: none;
          }

          .cta-large:hover {
            transform: none;
            box-shadow: none;
          }

          .faq-question:hover {
            background: none;
          }

          .toggle-btn:active {
            opacity: 0.8;
          }

          .cta-button:active {
            opacity: 0.9;
          }

          .faq-question:active {
            background: #f3f4f6;
          }
        }
      `}</style>
    </div>
  );
}

export default Pricing;
